import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Sparkles } from "lucide-react";

interface PlanOverviewCardProps {
  planName: string;
  isActive: boolean;
  onUpgrade: () => void;
  status?: 'trialing' | 'active' | 'past_due' | 'canceled';
  trialEndsAt?: string | null;
}

export const PlanOverviewCard: React.FC<PlanOverviewCardProps> = ({
  planName,
  isActive,
  onUpgrade,
  status = 'active',
  trialEndsAt,
}) => {
  const now = Date.now();
  const trialTime = trialEndsAt ? new Date(trialEndsAt).getTime() : null;
  const isExpiredTrial = status === 'trialing' && trialTime !== null && trialTime <= now;
  const daysLeft =
    trialTime !== null && trialTime > now
      ? Math.ceil((trialTime - now) / (1000 * 60 * 60 * 24))
      : 0;

  const renderBadge = () => {
    if (isExpiredTrial) {
      return (
        <Badge variant="destructive" className="rounded-full">
          Trial Expired
        </Badge>
      );
    }
    if (status === 'trialing') {
      return (
        <Badge className="rounded-full bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200">
          Trial ({daysLeft} {daysLeft === 1 ? 'day' : 'days'} left)
        </Badge>
      );
    }
    if (status === 'past_due') {
      return (
        <Badge variant="destructive" className="rounded-full">
          Past Due
        </Badge>
      );
    }
    return (
      <Badge
        className={
          isActive
            ? "rounded-full bg-purple-100 text-purple-700 hover:bg-purple-100"
            : "rounded-full bg-gray-100 text-gray-600 hover:bg-gray-100"
        }
      >
        {isActive ? "Active" : "Inactive"}
      </Badge>
    );
  };

  const getSubtitle = () => {
    if (isExpiredTrial) {
      return "14-day free trial expired • Upgrade your plan to continue";
    }
    if (status === 'trialing') {
      return `14-day free trial active • ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} remaining`;
    }
    if (status === 'past_due') {
      return "Payment is past due • Please update your billing details";
    }
    if (status === 'canceled') {
      return "Subscription canceled";
    }
    return isActive ? "Active subscription" : "Subscription inactive";
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-5">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-purple-100">
              <ShieldCheck className="h-8 w-8 text-purple-600" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                  Current Plan
                </span>
                {renderBadge()}
              </div>
              <h2 className="mt-1 text-3xl font-bold text-gray-900">{planName}</h2>
              <p className="mt-1 text-gray-500">{getSubtitle()}</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={onUpgrade}
            className="border-purple-500 text-purple-600 hover:bg-purple-50 hover:text-purple-700"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Upgrade Plan
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
